import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { FloatingPanel } from '@/components/ui/floating-panel'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { useTimelineStore } from '../stores/timeline-store'
import { useTimelineItemOverlayStore } from '../stores/timeline-item-overlay-store'
import { useSilenceRemovalDialogStore } from '../stores/silence-removal-dialog-store'
import { applySilenceRemovalToItems } from '../stores/actions/silence-removal-actions'
import {
  analyzeSilenceForItems,
  applySilencePreviewOverlays,
  clearSilenceRemovalOverlays,
  SILENCE_REMOVAL_ANALYZING_OVERLAY_ID,
  type SilenceRemovalSettings,
} from '../utils/silence-removal-preview'

const SILENCE_REMOVAL_PANEL_DEFAULT_BOUNDS = {
  x: -1,
  y: -1,
  width: 360,
  height: 420,
}

const SILENCE_REMOVAL_PANEL_STORAGE_KEY = 'fastcut.silence-removal-panel'

function toInputValue(value: number): string {
  return Number.isFinite(value) ? String(value) : ''
}

function areSettingsEqual(a: SilenceRemovalSettings, b: SilenceRemovalSettings): boolean {
  return (
    a.thresholdDb === b.thresholdDb &&
    a.minSilenceMs === b.minSilenceMs &&
    a.paddingMs === b.paddingMs &&
    a.windowMs === b.windowMs
  )
}

export const SilenceRemovalDialog = memo(function SilenceRemovalDialog() {
  const isOpen = useSilenceRemovalDialogStore((state) => state.isOpen)
  const itemIds = useSilenceRemovalDialogStore((state) => state.itemIds)
  const settings = useSilenceRemovalDialogStore((state) => state.settings)
  const rangesByItemId = useSilenceRemovalDialogStore((state) => state.rangesByItemId)
  const summary = useSilenceRemovalDialogStore((state) => state.summary)
  const isAnalyzing = useSilenceRemovalDialogStore((state) => state.isAnalyzing)
  const error = useSilenceRemovalDialogStore((state) => state.error)
  const close = useSilenceRemovalDialogStore((state) => state.close)
  const setSettings = useSilenceRemovalDialogStore((state) => state.setSettings)
  const setAnalyzing = useSilenceRemovalDialogStore((state) => state.setAnalyzing)
  const setAnalysisResult = useSilenceRemovalDialogStore((state) => state.setAnalysisResult)
  const setError = useSilenceRemovalDialogStore((state) => state.setError)
  const resetAnalysis = useSilenceRemovalDialogStore((state) => state.resetAnalysis)
  const upsertOverlay = useTimelineItemOverlayStore((state) => state.upsertOverlay)
  const [refreshToken, setRefreshToken] = useState(0)
  const [isApplying, setIsApplying] = useState(false)
  const previousItemIdsRef = useRef<string[]>([])
  const analyzedSettingsRef = useRef<SilenceRemovalSettings | null>(null)
  const items = useTimelineStore((state) => state.items)

  const selectedItems = useMemo(() => {
    const itemById = new Map(items.map((item) => [item.id, item]))
    return itemIds
      .map((itemId) => itemById.get(itemId))
      .filter((item): item is NonNullable<typeof item> => item !== undefined)
  }, [itemIds, items])

  const itemIdsWithAnyCaptions = useMemo(() => {
    const ids = new Set<string>()
    for (const item of items) {
      if (item.type === 'text' && item.captionSource?.clipId) {
        ids.add(item.captionSource.clipId)
      }
    }
    return ids
  }, [items])

  const itemsWithCaptions = useMemo(
    () => selectedItems.filter((item) => itemIdsWithAnyCaptions.has(item.id)),
    [itemIdsWithAnyCaptions, selectedItems],
  )
  const applicableItemIds = useMemo(
    () => selectedItems.filter((item) => !itemIdsWithAnyCaptions.has(item.id)).map((item) => item.id),
    [itemIdsWithAnyCaptions, selectedItems],
  )
  const canApplyRemoval = (summary?.totalRanges ?? 0) > 0 && applicableItemIds.length > 0

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const previousItemIds = previousItemIdsRef.current
    if (previousItemIds.length > 0) {
      clearSilenceRemovalOverlays(previousItemIds)
    }
    previousItemIdsRef.current = itemIds

    if (itemIds.length === 0) {
      analyzedSettingsRef.current = null
      resetAnalysis()
      return
    }

    let cancelled = false

    const run = async () => {
      setAnalyzing(true)
      setError(null)
      clearSilenceRemovalOverlays(itemIds)
      for (const itemId of itemIds) {
        upsertOverlay(itemId, {
          id: SILENCE_REMOVAL_ANALYZING_OVERLAY_ID,
          label: '检测静音中',
          tone: 'info',
        })
      }

      try {
        const rangesByItemId = await analyzeSilenceForItems(itemIds, settings)
        if (cancelled) {
          return
        }
        const nextSummary = applySilencePreviewOverlays(itemIds, rangesByItemId)
        analyzedSettingsRef.current = { ...settings }
        setAnalysisResult(rangesByItemId, nextSummary)
      } catch (analysisError) {
        if (cancelled) {
          return
        }
        analyzedSettingsRef.current = null
        clearSilenceRemovalOverlays(itemIds)
        setError(
          analysisError instanceof Error ? analysisError.message : '静音检测失败，请稍后重试。',
        )
      } finally {
        if (!cancelled) {
          setAnalyzing(false)
        }
      }
    }

    void run()

    return () => {
      cancelled = true
      clearSilenceRemovalOverlays(itemIds)
    }
  }, [
    isOpen,
    itemIds,
    refreshToken,
    resetAnalysis,
    setAnalyzing,
    setAnalysisResult,
    setError,
    settings,
    upsertOverlay,
  ])

  if (!isOpen) {
    return null
  }

  const handleSettingChange = (key: keyof SilenceRemovalSettings, rawValue: string) => {
    const parsed = Number(rawValue)
    if (!Number.isFinite(parsed)) {
      return
    }
    setSettings({ [key]: parsed })
  }

  const handleClose = () => {
    clearSilenceRemovalOverlays(itemIds)
    close()
  }

  const handleApply = async () => {
    if (!canApplyRemoval || isApplying) {
      return
    }

    const analyzedSettings = analyzedSettingsRef.current
    if (!analyzedSettings || !areSettingsEqual(analyzedSettings, settings)) {
      toast.info('参数已变更，请先重新检测后再应用。')
      return
    }

    setIsApplying(true)
    try {
      const applicableRangesByItemId = Object.fromEntries(
        applicableItemIds.map((itemId) => [itemId, rangesByItemId[itemId] ?? []]),
      )
      const result = applySilenceRemovalToItems(applicableItemIds, applicableRangesByItemId)
      clearSilenceRemovalOverlays(itemIds)
      if (result.processedItemCount === 0) {
        toast.info('当前预览没有可应用的静音片段')
      } else {
        const extraSkippedCount = itemsWithCaptions.length + result.skippedItemCount
        toast.success(
          extraSkippedCount > 0
            ? `已移除 ${result.removedSegmentCount} 段静音，收紧 ${result.shiftedItemCount} 个片段，跳过 ${extraSkippedCount} 个片段`
            : `已移除 ${result.removedSegmentCount} 段静音，收紧 ${result.shiftedItemCount} 个片段`,
        )
      }
      close()
    } catch (applyError) {
      toast.error(applyError instanceof Error ? applyError.message : '应用静音移除失败')
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <FloatingPanel
      title="静音移除预览"
      defaultBounds={SILENCE_REMOVAL_PANEL_DEFAULT_BOUNDS}
      minWidth={320}
      minHeight={360}
      storageKey={SILENCE_REMOVAL_PANEL_STORAGE_KEY}
      onClose={handleClose}
    >
      <section className="flex h-full flex-col gap-4 bg-background px-4 py-3 text-sm">
        <div className="space-y-1">
          <div className="text-sm font-medium text-foreground">已选片段</div>
          <div className="text-xs text-muted-foreground">
            {selectedItems.length > 0
              ? selectedItems.map((item) => item.label).join('、')
              : '当前没有可检测静音的片段。'}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="silence-threshold-db">阈值 dB</Label>
            <Input
              id="silence-threshold-db"
              type="number"
              step="1"
              value={toInputValue(settings.thresholdDb)}
              onChange={(event) => handleSettingChange('thresholdDb', event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="silence-window-ms">窗口 ms</Label>
            <Input
              id="silence-window-ms"
              type="number"
              step="10"
              value={toInputValue(settings.windowMs)}
              onChange={(event) => handleSettingChange('windowMs', event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="silence-min-ms">最小时长 ms</Label>
            <Input
              id="silence-min-ms"
              type="number"
              step="50"
              value={toInputValue(settings.minSilenceMs)}
              onChange={(event) => handleSettingChange('minSilenceMs', event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="silence-padding-ms">前后留白 ms</Label>
            <Input
              id="silence-padding-ms"
              type="number"
              step="20"
              value={toInputValue(settings.paddingMs)}
              onChange={(event) => handleSettingChange('paddingMs', event.target.value)}
            />
          </div>
        </div>

        <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            预览结果
          </div>
          <div className="mt-2 space-y-1 text-sm">
            <div>片段数：{summary?.itemCount ?? selectedItems.length}</div>
            <div>检测到静音的片段：{summary?.itemsWithSilence ?? 0}</div>
            <div>静音段数：{summary?.totalRanges ?? 0}</div>
            <div>静音总时长：{(summary?.totalDurationSec ?? 0).toFixed(1)}s</div>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            当前会先在片段内部真实移除静音并收紧剩余分段，不会做整轨 ripple delete。
          </div>
          {itemsWithCaptions.length > 0 && (
            <div className="mt-3 text-xs text-amber-600">
              当前选中片段中有自动字幕。为了避免把现有字幕替换流带坏，这一版会自动跳过这些片段，其余片段继续应用。
            </div>
          )}
          {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
        </div>

        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border/70 bg-background/70 p-3">
          <div className="space-y-2">
            {summary?.items.map((itemSummary) => {
              const item = selectedItems.find((candidate) => candidate.id === itemSummary.itemId)
              const itemRanges = rangesByItemId[itemSummary.itemId] ?? []
              return (
                <div key={itemSummary.itemId} className="rounded-md border border-border/60 p-2">
                  <div className="font-medium text-foreground">{item?.label ?? itemSummary.itemId}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {itemSummary.rangeCount > 0
                      ? `静音 ${itemSummary.rangeCount} 段，约 ${itemSummary.durationSec.toFixed(1)}s`
                      : '未检测到静音'}
                  </div>
                  {itemRanges.length > 0 && (
                    <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                      {itemRanges.slice(0, 4).map((range, index) => (
                        <div key={`${itemSummary.itemId}-${index}`}>
                          {`#${index + 1} ${range.startMs}ms - ${range.endMs}ms`}
                        </div>
                      ))}
                      {itemRanges.length > 4 && <div>{`还有 ${itemRanges.length - 4} 段未展开`}</div>}
                    </div>
                  )}
                </div>
              )
            })}
            {!summary && !isAnalyzing && !error && (
              <div className="text-xs text-muted-foreground">调整参数后会自动重新检测。</div>
            )}
            {isAnalyzing && <div className="text-xs text-muted-foreground">正在根据波形峰值检测静音…</div>}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" onClick={() => setRefreshToken((token) => token + 1)}>
            重新检测
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handleClose}>
              关闭
            </Button>
            <Button onClick={handleApply} disabled={!canApplyRemoval || isAnalyzing || isApplying}>
              {isApplying ? '应用中...' : applicableItemIds.length < selectedItems.length ? '应用可用片段' : '应用预览'}
            </Button>
          </div>
        </div>
      </section>
    </FloatingPanel>
  )
})